import React, { useEffect, useMemo, useState } from "react";
import AccountingChart from "../components/AccountingChart";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Button,
  Spinner,
  Pagination,
  ButtonGroup,
  Badge,
} from "react-bootstrap";
import api from "../api";
import ImageGalleryModal from "../components/ImageGalleryModal";

/**
 * Shopify Accounting Dashboard
 * ---------------------------------------------------------
 * Accounting model:
 * - total_sales = ITEM SUBTOTAL ONLY server-calculated
 * - profit is computed server-side only
 * - shipping cost shown is allocated shipping
 * - buyer shipping & shipping delta are informational
 * - modern dark UI to match Budget / Accounting pages
 * ---------------------------------------------------------
 */

const pageStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)",
  color: "#e5e7eb",
};

const shellStyle = {
  maxWidth: 1800,
  margin: "0 auto",
};

const cardStyle = {
  background: "rgba(15, 23, 42, 0.86)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: 22,
  color: "#e5e7eb",
  boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
  backdropFilter: "blur(10px)",
};

const cardSoftStyle = {
  background: "rgba(30, 41, 59, 0.82)",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  borderRadius: 20,
  color: "#e5e7eb",
};

const heroStyle = {
  background:
    "linear-gradient(135deg, rgba(34,197,94,0.34), rgba(15,23,42,0.94) 42%, rgba(37,99,235,0.20))",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 28,
  color: "#f8fafc",
  boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
};

const inputStyle = {
  background: "#020617",
  color: "#e5e7eb",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  borderRadius: 12,
};

const mutedStyle = {
  color: "#94a3b8",
};

const dividerStyle = {
  borderColor: "rgba(148, 163, 184, 0.14)",
};

const pillStyle = {
  border: "1px solid rgba(148, 163, 184, 0.20)",
  background: "rgba(15, 23, 42, 0.72)",
  color: "#cbd5e1",
  borderRadius: 999,
  padding: "6px 11px",
  fontSize: 12,
  fontWeight: 700,
};

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (value) => {
  const num = cleanNumber(value);

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value) => {
  const num = cleanNumber(value);
  return `${num.toFixed(2)}%`;
};

const getMoneyColor = (value) => {
  const num = cleanNumber(value);
  if (num > 0) return "#22c55e";
  if (num < 0) return "#ef4444";
  return "#cbd5e1";
};

const getProgressColor = (percent) => {
  if (percent >= 100) return "linear-gradient(90deg, #22c55e, #16a34a)";
  if (percent >= 75) return "linear-gradient(90deg, #38bdf8, #2563eb)";
  if (percent >= 40) return "linear-gradient(90deg, #f59e0b, #f97316)";
  return "linear-gradient(90deg, #ef4444, #dc2626)";
};

const getSafeDate = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getImagesFromRow = (row, group) => {
  if (group !== "order") return [];

  return row.image_list
    ? String(row.image_list)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
};

function AccountingShopify() {
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingShippingOrderId, setSavingShippingOrderId] = useState(null);
  const [error, setError] = useState("");

  const [group, setGroup] = useState("order");
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");

  const [summary, setSummary] = useState({
    totalSales: 0,
    totalCost: 0,
    totalFees: 0,
    buyerShippingPaid: 0,
    allocatedShippingCost: 0,
    shippingDelta: 0,
    totalProfit: 0,
    totalQuantity: 0,
    profitMargin: 0,
    storeCreditUsed: 0,
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [galleryShow, setGalleryShow] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const roi = useMemo(() => {
    if (summary.totalCost <= 0) return 0;
    return (summary.totalProfit / summary.totalCost) * 100;
  }, [summary.totalCost, summary.totalProfit]);

  const averageProfitPerUnit = useMemo(() => {
    if (summary.totalQuantity <= 0) return 0;
    return summary.totalProfit / summary.totalQuantity;
  }, [summary.totalProfit, summary.totalQuantity]);

  const costPercentOfSales = useMemo(() => {
    if (summary.totalSales <= 0) return 0;
    return (summary.totalCost / summary.totalSales) * 100;
  }, [summary.totalSales, summary.totalCost]);

  const feePercentOfSales = useMemo(() => {
    if (summary.totalSales <= 0) return 0;
    return (summary.totalFees / summary.totalSales) * 100;
  }, [summary.totalSales, summary.totalFees]);

  const shippingCostPercentOfSales = useMemo(() => {
    if (summary.totalSales <= 0) return 0;
    return (summary.allocatedShippingCost / summary.totalSales) * 100;
  }, [summary.totalSales, summary.allocatedShippingCost]);

  const storeCreditPercentOfSales = useMemo(() => {
    if (summary.totalSales <= 0) return 0;
    return (summary.storeCreditUsed / summary.totalSales) * 100;
  }, [summary.totalSales, summary.storeCreditUsed]);

  const accountingStatus = useMemo(() => {
    if (summary.totalProfit > 0) {
      return {
        label: "Profitable",
        icon: "✅",
        tone: "#22c55e",
        bg: "rgba(34, 197, 94, 0.14)",
        border: "rgba(34, 197, 94, 0.35)",
        message: `${formatMoney(summary.totalProfit)} net profit with ${formatPercent(summary.profitMargin)} margin.`,
      };
    }

    if (summary.totalProfit < 0) {
      return {
        label: "Losing Money",
        icon: "⚠️",
        tone: "#ef4444",
        bg: "rgba(239, 68, 68, 0.14)",
        border: "rgba(239, 68, 68, 0.35)",
        message: `${formatMoney(summary.totalProfit)} net profit. Review COGS, fees, shipping, and store credit.`,
      };
    }

    return {
      label: "Break Even",
      icon: "📊",
      tone: "#94a3b8",
      bg: "rgba(148, 163, 184, 0.12)",
      border: "rgba(148, 163, 184, 0.25)",
      message: "No profit or loss for the selected Shopify period yet.",
    };
  }, [summary.totalProfit, summary.profitMargin]);

  async function fetchProfit(
    newPage = page,
    periodArg = period,
    startArg = startDate,
    endArg = endDate,
    startDateTimeArg = startDateTime,
    endDateTimeArg = endDateTime
  ) {
    setLoading(true);
    setError("");

    try {
      const query = new URLSearchParams({
        group,
        period: periodArg || "",
        start: startArg || "",
        end: endArg || "",
        startDateTime: startDateTimeArg || "",
        endDateTime: endDateTimeArg || "",
        page: newPage,
        limit: 50,
      }).toString();

      const res = await api.get(`/accounting/shopify/profit?${query}`);
      const rows = res.data.data || [];

      setData(rows);

      const fixedChart = (res.data.chart || []).map((row) => {
        let d;

        if (periodArg === "day" || periodArg === "custom_hours") {
          const hour = Number(row.date || 0);
          d = `${hour}:00`;
        } else {
          d = String(row.date || "").trim();
          if (d.includes(" ")) d = d.split(" ")[0];
          if (d.includes("T")) d = d.split("T")[0];
        }

        return {
          ...row,
          date: d,
        };
      });

      setChartData(fixedChart);
      setPage(res.data.currentPage || 1);
      setTotalPages(res.data.totalPages || 1);

      const s = res.data.summary || {};

      setSummary({
        totalSales: cleanNumber(s.total_sales),
        totalCost: cleanNumber(s.total_cost),
        totalFees: cleanNumber(s.platform_fees),
        buyerShippingPaid: cleanNumber(s.buyer_shipping_paid),
        allocatedShippingCost: cleanNumber(s.allocated_shipping_cost),
        shippingDelta: cleanNumber(s.shipping_delta),
        totalProfit: cleanNumber(s.total_profit),
        totalQuantity: Number.parseInt(s.total_quantity || 0, 10),
        storeCreditUsed: cleanNumber(s.store_credit_used),
        profitMargin: cleanNumber(s.profit_margin),
      });
    } catch (err) {
      console.error("Shopify accounting error:", err.response?.data || err.message);

      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          "Failed to load Shopify accounting data."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProfit(1, period, startDate, endDate, startDateTime, endDateTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, startDateTime, endDateTime, group]);

  async function saveShippingCost(orderId, value) {
    const cost = Number.parseFloat(value);
    if (Number.isNaN(cost)) return;

    try {
      setSavingShippingOrderId(orderId);

      await api.post("/accounting/shopify/shipping-cost", {
        orderId,
        cost,
      });

      await fetchProfit(page, period, startDate, endDate, startDateTime, endDateTime);
    } catch (err) {
      console.error("Failed to save shipping cost:", err.response?.data || err.message);
      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          "Failed to save shipping cost."
      );
    } finally {
      setSavingShippingOrderId(null);
    }
  }

  const applyFilters = async () => {
    await fetchProfit(1, period, startDate, endDate, startDateTime, endDateTime);
  };

  const setQuickPeriod = (nextPeriod) => {
    setStartDate("");
    setEndDate("");
    setStartDateTime("");
    setEndDateTime("");
    setPeriod(nextPeriod);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 7;
    const start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    const adjustedStart = Math.max(1, end - maxVisible + 1);

    for (let p = adjustedStart; p <= end; p += 1) {
      items.push(
        <Pagination.Item key={p} active={p === page} onClick={() => fetchProfit(p)}>
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
        <div style={mutedStyle}>
          Page <strong style={{ color: "#f8fafc" }}>{page}</strong> of{" "}
          <strong style={{ color: "#f8fafc" }}>{totalPages}</strong>
        </div>

        <Pagination className="mb-0 shopify-accounting-pagination">
          <Pagination.First disabled={page === 1} onClick={() => fetchProfit(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => fetchProfit(page - 1)} />
          {adjustedStart > 1 && <Pagination.Ellipsis disabled />}
          {items}
          {end < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next disabled={page === totalPages} onClick={() => fetchProfit(page + 1)} />
          <Pagination.Last disabled={page === totalPages} onClick={() => fetchProfit(totalPages)} />
        </Pagination>
      </div>
    );
  };

  const MiniMetric = ({ label, value, color = "#e5e7eb" }) => {
    return (
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 16,
          background: "rgba(2, 6, 23, 0.45)",
          border: "1px solid rgba(148, 163, 184, 0.12)",
        }}
      >
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 3 }}>
          {label}
        </div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
      </div>
    );
  };

  const ProgressBar = ({ value, height = 12 }) => {
    const clean = Math.min(100, Math.max(0, cleanNumber(value)));

    return (
      <div
        style={{
          width: "100%",
          height,
          borderRadius: 999,
          background: "rgba(15, 23, 42, 0.82)",
          border: "1px solid rgba(148, 163, 184, 0.12)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${clean}%`,
            height: "100%",
            background: getProgressColor(clean),
            borderRadius: 999,
            transition: "width 240ms ease",
          }}
        />
      </div>
    );
  };

  const SectionHeader = ({ title, subtitle, action }) => {
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1" style={{ color: "#f8fafc", fontWeight: 850 }}>
            {title}
          </h4>
          {subtitle && <p className="mb-0" style={mutedStyle}>{subtitle}</p>}
        </div>
        {action}
      </div>
    );
  };

  const StatCard = ({
    label,
    value,
    subtext,
    accent = "#38bdf8",
    icon = "📊",
    valueColor,
  }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: "none",
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || "#f8fafc",
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: "#94a3b8", fontSize: 13 }}>
                {subtext}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ImageCell = ({ row }) => {
    const images = getImagesFromRow(row, group);
    const firstImage = images[0];
    const extra = images.length - 1;

    return (
      <td style={{ width: 84, minWidth: 84 }}>
        <div
          style={{
            position: "relative",
            display: "inline-block",
            cursor: firstImage ? "pointer" : "default",
          }}
          onClick={() => {
            if (!images.length) return;

            setGalleryImages(images);
            setGalleryIndex(0);
            setGalleryShow(true);
          }}
        >
          {firstImage ? (
            <img
              src={firstImage}
              alt={row.group_key || "Shopify order"}
              style={{
                width: 60,
                height: 60,
                objectFit: "contain",
                borderRadius: 14,
                border: "1px solid rgba(148, 163, 184, 0.22)",
                background: "rgba(2, 6, 23, 0.60)",
                padding: 4,
              }}
            />
          ) : (
            <div
              style={{
                width: 60,
                height: 60,
                background: "rgba(2, 6, 23, 0.60)",
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "0.8rem",
                border: "1px solid rgba(148, 163, 184, 0.18)",
              }}
            >
              N/A
            </div>
          )}

          {extra > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: -7,
                right: -7,
                background: "#16a34a",
                color: "white",
                borderRadius: 999,
                padding: "2px 7px",
                fontSize: "0.72rem",
                border: "1px solid rgba(255,255,255,0.85)",
                fontWeight: 800,
              }}
            >
              +{extra}
            </div>
          )}
        </div>
      </td>
    );
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          .shopify-accounting-dark input,
          .shopify-accounting-dark select,
          .shopify-accounting-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .shopify-accounting-dark input::placeholder,
          .shopify-accounting-dark textarea::placeholder {
            color: #64748b !important;
          }

          .shopify-accounting-dark input:focus,
          .shopify-accounting-dark select:focus,
          .shopify-accounting-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.18) !important;
            border-color: rgba(34, 197, 94, 0.75) !important;
          }

          .shopify-accounting-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .shopify-accounting-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .shopify-accounting-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
            margin-bottom: 0;
          }

          .shopify-accounting-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .shopify-accounting-dark .table tbody td {
            vertical-align: middle;
            border-color: rgba(148, 163, 184, 0.10);
            white-space: nowrap;
          }

          .shopify-accounting-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
          }

          .shopify-accounting-dark .pagination .page-link {
            background: rgba(15, 23, 42, 0.88);
            border-color: rgba(148, 163, 184, 0.18);
            color: #cbd5e1;
            font-weight: 700;
          }

          .shopify-accounting-dark .pagination .page-item.active .page-link {
            background: #16a34a;
            border-color: #16a34a;
            color: #ffffff;
          }

          .shopify-accounting-dark .pagination .page-item.disabled .page-link {
            background: rgba(15, 23, 42, 0.55);
            border-color: rgba(148, 163, 184, 0.10);
            color: #64748b;
          }

          .shopify-accounting-dark .badge {
            border-radius: 999px;
            padding: 6px 9px;
          }

          .shipping-cost-input {
            width: 100px !important;
            min-width: 100px;
          }
        `}
      </style>

      <Container fluid className="py-4 shopify-accounting-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: "#bbf7d0", borderColor: "rgba(34,197,94,0.38)" }}>
                  Shopify Accounting
                </span>
                <span style={{ ...pillStyle, color: "#bfdbfe", borderColor: "rgba(59,130,246,0.38)" }}>
                  Item Subtotal Sales
                </span>
                <span style={{ ...pillStyle, color: "#fef3c7", borderColor: "rgba(245,158,11,0.38)" }}>
                  Allocated Shipping
                </span>
                <span style={{ ...pillStyle, color: "#ddd6fe", borderColor: "rgba(168,85,247,0.38)" }}>
                  Stream Hour Reporting
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: "#f8fafc",
                  fontWeight: 950,
                  letterSpacing: "-0.04em",
                  fontSize: "clamp(2rem, 4vw, 4.35rem)",
                  lineHeight: 0.95,
                }}
              >
                Shopify Control Center
              </h1>

              <p className="mb-0" style={{ color: "#cbd5e1", maxWidth: 940, fontSize: 17 }}>
                Review Shopify sales, COGS, platform fees, buyer shipping, allocated shipping,
                store credit, ROI, and stream-hour profit windows in one clean dashboard.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 310,
              }}
            >
              <div className="mb-2" style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Current View
              </div>

              <div className="mb-3" style={{ color: "#f8fafc", fontSize: 26, fontWeight: 900 }}>
                {period === "all"
                  ? "All Time"
                  : period === "custom"
                    ? "Custom Range"
                    : period === "custom_hours"
                      ? "Stream Hours"
                      : `This ${period}`}
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric label="Rows" value={data.length.toLocaleString()} color="#38bdf8" />
                </Col>
                <Col xs={6}>
                  <MiniMetric label="Page" value={`${page} / ${totalPages}`} color="#c4b5fd" />
                </Col>
              </Row>

              <div className="d-grid mt-3">
                <Button variant="success" onClick={applyFilters} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Refreshing...
                    </>
                  ) : (
                    "Refresh Shopify Accounting"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="alert mb-4"
            style={{
              background: "rgba(127,29,29,0.70)",
              border: "1px solid rgba(248,113,113,0.35)",
              color: "#fee2e2",
              borderRadius: 18,
            }}
          >
            <strong>Shopify accounting could not load.</strong>
            <div>{error}</div>
            <Button size="sm" variant="light" className="mt-2" onClick={applyFilters}>
              Retry
            </Button>
          </div>
        )}

        <div className="mb-4" style={cardStyle}>
          <div className="p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h3 className="mb-0" style={{ color: "#f8fafc", fontWeight: 900 }}>
                    {accountingStatus.icon} Shopify Profit Status
                  </h3>

                  <span
                    style={{
                      border: `1px solid ${accountingStatus.border}`,
                      background: accountingStatus.bg,
                      color: accountingStatus.tone,
                      borderRadius: 999,
                      padding: "7px 12px",
                      fontSize: 12,
                      fontWeight: 850,
                    }}
                  >
                    {accountingStatus.label}
                  </span>
                </div>

                <p className="mb-0" style={mutedStyle}>
                  {accountingStatus.message}
                </p>
              </div>

              <div className="text-end">
                <div style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Grouping
                </div>
                <div style={{ color: "#f8fafc", fontSize: 26, fontWeight: 900 }}>
                  {group === "sku" ? "SKU" : "Order"}
                </div>
              </div>
            </div>

            <Row className="g-2">
              <Col md={3}>
                <MiniMetric label="Total Sales" value={formatMoney(summary.totalSales)} color="#38bdf8" />
              </Col>
              <Col md={3}>
                <MiniMetric label="Net Profit" value={formatMoney(summary.totalProfit)} color={getMoneyColor(summary.totalProfit)} />
              </Col>
              <Col md={3}>
                <MiniMetric label="Profit Margin" value={formatPercent(summary.profitMargin)} color="#22c55e" />
              </Col>
              <Col md={3}>
                <MiniMetric label="ROI" value={formatPercent(roi)} color="#c4b5fd" />
              </Col>
            </Row>
          </div>
        </div>

        <div className="p-4 mb-4" style={cardStyle}>
          <SectionHeader
            title="Filters"
            subtitle="Choose grouping, timeframe, custom dates, or exact stream-hour windows."
            action={
              <Button variant="outline-success" size="sm" onClick={applyFilters} disabled={loading}>
                {loading ? "Loading..." : "Apply Filters"}
              </Button>
            }
          />

          <Row className="align-items-end g-3">
            <Col lg={3} md={6}>
              <Form.Label>Group Report By</Form.Label>
              <Form.Select
                style={inputStyle}
                value={group}
                onChange={(e) => setGroup(e.target.value)}
              >
                <option value="sku">Group by SKU</option>
                <option value="order">Group by Order</option>
              </Form.Select>
            </Col>

            <Col lg={6} md={12}>
              <Form.Label>Time Period</Form.Label>
              <div>
                <ButtonGroup className="flex-wrap">
                  {["all", "day", "week", "month"].map((p) => (
                    <Button
                      key={p}
                      variant={period === p ? "success" : "outline-success"}
                      onClick={() => setQuickPeriod(p)}
                    >
                      {p === "all"
                        ? "All Time"
                        : p === "day"
                          ? "Today"
                          : p === "week"
                            ? "This Week"
                            : "This Month"}
                    </Button>
                  ))}

                  <Button
                    variant={period === "custom" ? "primary" : "outline-secondary"}
                    onClick={() => {
                      setStartDateTime("");
                      setEndDateTime("");
                      setPeriod("custom");
                    }}
                  >
                    Custom
                  </Button>

                  <Button
                    variant={period === "custom_hours" ? "warning" : "outline-warning"}
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setPeriod("custom_hours");
                    }}
                  >
                    Stream Hours
                  </Button>
                </ButtonGroup>
              </div>
            </Col>

            {period === "custom" && (
              <>
                <Col lg={2} md={6}>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    style={inputStyle}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Col>

                <Col lg={2} md={6}>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    style={inputStyle}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Col>
              </>
            )}

            {period === "custom_hours" && (
              <>
                <Col lg={3} md={6}>
                  <Form.Label>Stream Start</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    style={inputStyle}
                    value={startDateTime}
                    onChange={(e) => setStartDateTime(e.target.value)}
                  />
                </Col>

                <Col lg={3} md={6}>
                  <Form.Label>Stream End</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    style={inputStyle}
                    value={endDateTime}
                    onChange={(e) => setEndDateTime(e.target.value)}
                  />
                </Col>
              </>
            )}
          </Row>
        </div>

        <Row className="mb-4">
          <StatCard
            label="Total Sales"
            value={formatMoney(summary.totalSales)}
            subtext={`${summary.totalQuantity.toLocaleString()} total units sold`}
            accent="#38bdf8"
            icon="💰"
          />

          <StatCard
            label="COGS"
            value={formatMoney(summary.totalCost)}
            subtext={`${formatPercent(costPercentOfSales)} of sales`}
            accent="#f59e0b"
            icon="📦"
            valueColor="#fbbf24"
          />

          <StatCard
            label="Buyer Shipping"
            value={formatMoney(summary.buyerShippingPaid)}
            subtext="Amount customer paid for shipping"
            accent="#06b6d4"
            icon="📬"
            valueColor="#67e8f9"
          />

          <StatCard
            label="Allocated Shipping"
            value={formatMoney(summary.allocatedShippingCost)}
            subtext={`${formatPercent(shippingCostPercentOfSales)} of sales`}
            accent="#64748b"
            icon="🚚"
          />

          <StatCard
            label="Shipping Delta"
            value={formatMoney(summary.shippingDelta)}
            subtext="Buyer shipping minus allocated cost"
            accent={summary.shippingDelta >= 0 ? "#22c55e" : "#ef4444"}
            icon="⚖️"
            valueColor={getMoneyColor(summary.shippingDelta)}
          />

          <StatCard
            label="Platform Fees"
            value={formatMoney(summary.totalFees)}
            subtext={`${formatPercent(feePercentOfSales)} of sales`}
            accent="#ef4444"
            icon="💳"
            valueColor="#fca5a5"
          />

          <StatCard
            label="Store Credit"
            value={formatMoney(summary.storeCreditUsed)}
            subtext={`${formatPercent(storeCreditPercentOfSales)} of sales`}
            accent="#a855f7"
            icon="🎟️"
            valueColor="#c4b5fd"
          />

          <StatCard
            label="Net Profit"
            value={formatMoney(summary.totalProfit)}
            subtext={`${formatPercent(summary.profitMargin)} margin · ${formatPercent(roi)} ROI`}
            accent="#22c55e"
            icon="✅"
            valueColor={getMoneyColor(summary.totalProfit)}
          />
        </Row>

        <Row className="mb-4">
          <Col xl={7} className="mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <SectionHeader
                title="Profit Flow"
                subtitle="Shopify money breakdown from item sales through net profit."
              />

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>COGS Percent of Sales</span>
                  <span style={{ color: "#fbbf24", fontWeight: 800 }}>
                    {formatMoney(summary.totalCost)} · {formatPercent(costPercentOfSales)}
                  </span>
                </div>
                <ProgressBar value={costPercentOfSales} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Fees Percent of Sales</span>
                  <span style={{ color: "#fca5a5", fontWeight: 800 }}>
                    {formatMoney(summary.totalFees)} · {formatPercent(feePercentOfSales)}
                  </span>
                </div>
                <ProgressBar value={feePercentOfSales} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Allocated Shipping Percent of Sales</span>
                  <span style={{ color: "#cbd5e1", fontWeight: 800 }}>
                    {formatMoney(summary.allocatedShippingCost)} · {formatPercent(shippingCostPercentOfSales)}
                  </span>
                </div>
                <ProgressBar value={shippingCostPercentOfSales} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Store Credit Percent of Sales</span>
                  <span style={{ color: "#c4b5fd", fontWeight: 800 }}>
                    {formatMoney(summary.storeCreditUsed)} · {formatPercent(storeCreditPercentOfSales)}
                  </span>
                </div>
                <ProgressBar value={storeCreditPercentOfSales} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Profit Margin</span>
                  <span style={{ color: getMoneyColor(summary.totalProfit), fontWeight: 800 }}>
                    {formatMoney(summary.totalProfit)} · {formatPercent(summary.profitMargin)}
                  </span>
                </div>
                <ProgressBar value={summary.profitMargin} />
              </div>

              <hr style={dividerStyle} />

              <Row className="g-2">
                <Col md={4}>
                  <MiniMetric label="ROI" value={formatPercent(roi)} color="#c4b5fd" />
                </Col>
                <Col md={4}>
                  <MiniMetric label="Avg Profit / Unit" value={formatMoney(averageProfitPerUnit)} color={getMoneyColor(averageProfitPerUnit)} />
                </Col>
                <Col md={4}>
                  <MiniMetric label="Shipping Delta" value={formatMoney(summary.shippingDelta)} color={getMoneyColor(summary.shippingDelta)} />
                </Col>
              </Row>
            </div>
          </Col>

          <Col xl={5} className="mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <SectionHeader
                title="Report Setup"
                subtitle="Your current Shopify accounting view settings."
              />

              <Row className="g-2 mb-3">
                <Col md={6}>
                  <MiniMetric label="Platform" value="Shopify" color="#86efac" />
                </Col>
                <Col md={6}>
                  <MiniMetric label="Group" value={group === "sku" ? "SKU" : "Order"} color="#c4b5fd" />
                </Col>
                <Col md={6}>
                  <MiniMetric
                    label="Period"
                    value={
                      period === "all"
                        ? "All Time"
                        : period === "custom"
                          ? "Custom"
                          : period === "custom_hours"
                            ? "Stream Hours"
                            : period
                    }
                    color="#fcd34d"
                  />
                </Col>
                <Col md={6}>
                  <MiniMetric label="Rows Loaded" value={data.length.toLocaleString()} color="#38bdf8" />
                </Col>
              </Row>

              {period === "custom_hours" ? (
                <div
                  className="p-3"
                  style={{
                    borderRadius: 16,
                    background: "rgba(245,158,11,0.10)",
                    border: "1px solid rgba(245,158,11,0.20)",
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#f8fafc" }}>Stream Hour Window</div>
                  <div style={{ ...mutedStyle, fontSize: 13, marginTop: 4 }}>
                    {startDateTime || "No start time"} to {endDateTime || "No end time"}
                  </div>
                </div>
              ) : period === "custom" ? (
                <div
                  className="p-3"
                  style={{
                    borderRadius: 16,
                    background: "rgba(59,130,246,0.10)",
                    border: "1px solid rgba(59,130,246,0.20)",
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#f8fafc" }}>Custom Date Range</div>
                  <div style={{ ...mutedStyle, fontSize: 13, marginTop: 4 }}>
                    {startDate || "No start date"} to {endDate || "No end date"}
                  </div>
                </div>
              ) : (
                <div
                  className="p-3"
                  style={{
                    borderRadius: 16,
                    background: "rgba(34,197,94,0.10)",
                    border: "1px solid rgba(34,197,94,0.20)",
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#f8fafc" }}>Auto Date Range</div>
                  <div style={{ ...mutedStyle, fontSize: 13, marginTop: 4 }}>
                    The backend controls this period and returns the matching chart, summary, and table.
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        <div className="p-4 mb-4" style={cardStyle}>
          <SectionHeader
            title="Shopify Sales vs Net Profit"
            subtitle="Chart data comes from the same Shopify accounting report response as the table."
          />

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="success" />
              <div className="mt-3" style={mutedStyle}>
                Loading Shopify accounting chart...
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "rgba(2, 6, 23, 0.30)",
                border: "1px solid rgba(148, 163, 184, 0.10)",
                borderRadius: 18,
                padding: 12,
              }}
            >
              <AccountingChart
                chartData={chartData}
                period={period === "all" ? "" : period}
                isHourly={period === "day" || period === "custom_hours"}
              />
            </div>
          )}
        </div>

        <div className="p-4" style={cardStyle}>
          <SectionHeader
            title="Shopify Profit Breakdown"
            subtitle={group === "sku" ? "Grouped by SKU." : "Grouped by order with editable label shipping cost."}
            action={
              <Button variant="outline-success" size="sm" onClick={applyFilters} disabled={loading}>
                {loading ? "Refreshing..." : "Refresh Table"}
              </Button>
            }
          />

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="success" />
              <div className="mt-3" style={mutedStyle}>
                Loading Shopify accounting rows...
              </div>
            </div>
          ) : data.length === 0 ? (
            <div
              className="p-4 text-center"
              style={{
                borderRadius: 18,
                background: "rgba(148,163,184,0.10)",
                border: "1px solid rgba(148,163,184,0.16)",
                color: "#cbd5e1",
              }}
            >
              No Shopify accounting rows found for this filter.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover responsive className="align-middle">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>{group === "sku" ? "SKU" : "Order ID"}</th>
                      {group === "order" && <th>Order #</th>}
                      <th className="text-end">Sales</th>
                      <th className="text-end">COGS</th>
                      <th className="text-end">Fees</th>
                      {group === "order" && <th className="text-end">Store Credit</th>}
                      <th className="text-end">Buyer Ship</th>
                      <th>Ship Cost</th>
                      <th className="text-end">Ship Δ</th>
                      <th className="text-end">Profit</th>
                      <th className="text-end">Margin</th>
                      <th className="text-end">ROI</th>
                      <th className="text-end">Qty</th>
                      <th>Last Order</th>
                    </tr>
                  </thead>

                  <tbody>
                    {data.map((row, i) => {
                      const rowSales = cleanNumber(row.total_sales);
                      const rowCost = cleanNumber(row.total_cost);
                      const rowProfit = cleanNumber(row.total_profit);
                      const rowMargin = rowSales > 0 ? (rowProfit / rowSales) * 100 : 0;
                      const rowRoi = rowCost > 0 ? (rowProfit / rowCost) * 100 : null;
                      const shippingDelta = cleanNumber(row.shipping_delta);
                      const storeCreditUsed = cleanNumber(row.store_credit_used);

                      return (
                        <tr key={`${row.group_key || "row"}-${i}`}>
                          <ImageCell row={row} />

                          <td>
                            <div style={{ color: "#f8fafc", fontWeight: 850 }}>
                              {row.group_key || "-"}
                            </div>
                          </td>

                          {group === "order" && (
                            <td>
                              <div style={{ color: "#f8fafc", fontWeight: 850 }}>
                                {row.shopify_order_number ? `#${row.shopify_order_number}` : "-"}
                              </div>

                              {row.combined_order_numbers &&
                                (() => {
                                  const others = String(row.combined_order_numbers)
                                    .split(", ")
                                    .filter((n) => n !== `#${row.shopify_order_number}`);

                                  if (!others.length) return null;

                                  return (
                                    <div style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
                                      Combined: {others.join(", ")}
                                    </div>
                                  );
                                })()}
                            </td>
                          )}

                          <td className="text-end">{formatMoney(rowSales)}</td>
                          <td className="text-end">{formatMoney(rowCost)}</td>
                          <td className="text-end">{formatMoney(row.platform_fees)}</td>

                          {group === "order" && (
                            <td className="text-end">
                              {storeCreditUsed > 0 ? (
                                <strong style={{ color: "#fca5a5" }}>
                                  -{formatMoney(storeCreditUsed)}
                                </strong>
                              ) : (
                                <span style={mutedStyle}>-</span>
                              )}
                            </td>
                          )}

                          <td className="text-end">{formatMoney(row.buyer_shipping_paid)}</td>

                          <td>
                            {group === "order" ? (
                              <div>
                                <Form.Control
                                  className="shipping-cost-input"
                                  type="number"
                                  step="0.01"
                                  defaultValue={cleanNumber(row.shipping_cost)}
                                  disabled={savingShippingOrderId === row.group_key}
                                  onBlur={(e) => saveShippingCost(row.group_key, e.target.value)}
                                />

                                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 5 }}>
                                  Allocated: {formatMoney(row.allocated_shipping_cost)}
                                </div>

                                {Number(row.is_combined_shipment) === 1 && (
                                  <Badge
                                    bg="info"
                                    className="mt-1"
                                    style={{
                                      background: "rgba(14,165,233,0.18)",
                                      color: "#bae6fd",
                                      border: "1px solid rgba(14,165,233,0.30)",
                                    }}
                                  >
                                    📦 Combined ({row.shipment_order_count})
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              formatMoney(row.allocated_shipping_cost)
                            )}
                          </td>

                          <td className="text-end">
                            <strong style={{ color: getMoneyColor(shippingDelta) }}>
                              {formatMoney(shippingDelta)}
                            </strong>
                          </td>

                          <td className="text-end">
                            <strong style={{ color: getMoneyColor(rowProfit) }}>
                              {formatMoney(rowProfit)}
                            </strong>
                          </td>

                          <td className="text-end">{formatPercent(rowMargin)}</td>

                          <td className="text-end">
                            {rowRoi === null ? "—" : formatPercent(rowRoi)}
                          </td>

                          <td className="text-end">{cleanNumber(row.quantity).toLocaleString()}</td>
                          <td>{getSafeDate(row.last_order_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {renderPagination()}
            </>
          )}
        </div>

        <ImageGalleryModal
          show={galleryShow}
          onHide={() => setGalleryShow(false)}
          images={galleryImages}
          index={galleryIndex}
        />
      </Container>
    </div>
  );
}

export default AccountingShopify;